'use client';

import localFont from "next/font/local";
import { config } from '@fortawesome/fontawesome-svg-core';
import "./styles/globals.css";
import { Provider } from 'react-redux';
import store from '@/app/store'; // Adjust the import path as necessary
import ThemeProvider from '@/app/features/theme/components/ThemeProvider'; // Import the Theme Provider component
import LanguageProvider from '@/app/features/language/components/LanguageProvider'; // Import the Language Provider component
import AuthProvider from '@/app/features/auth/components/AuthProvider'; // Import the Auth Provider
import BrandingProvider from '@/app/features/branding/components/BrandingProvider'; // Hydrates branding slice from /api/config/get-main_config
import { ConfirmDialogProvider } from '@/app/providers/ConfirmDialogProvider'; // Import the Confirm Dialog Provider
// Shadcn Toaster removed — the app standardized on Sonner (mounted via NotificationProvider).
import { NotificationProvider } from '@/app/features/notifications/components/NotificationProvider'; // Import the Notification Provider
import { CookieConsentProvider } from '@/app/features/cookie-consent/components/CookieConsentProvider'; // Import the Cookie Consent Provider
import { ChatButton } from '@/app/features/chat/components/ChatButton'; // Import the Chat Button

config.autoAddCss = false; // Tell Font Awesome to skip adding the CSS automatically since it's already imported

// Synchronous boot script — runs before React hydrates so the page paints in
// the correct theme colors on first load. Reads the last applied theme from
// localStorage (written by applyThemeToDocument()) and writes the matching
// CSS variables + class onto <html> in-place. Falls back silently on any
// error so a corrupt cache never blocks rendering.
//
// The color palette below MUST stay in sync with the one in
// app/features/theme/utils/simpleThemeUtils.ts. Duplication is intentional —
// this script can't import application code (it's a string at boot time).
const themeBootScript = `
(function () {
  try {
    var raw = localStorage.getItem('sm-last-applied-theme');
    if (!raw) return;
    var parsed = JSON.parse(raw);
    var mode = parsed && parsed.mode;
    var colorTheme = parsed && parsed.colorTheme;
    if (mode !== 'light' && mode !== 'dark') return;

    var palettes = {
      yellow: { light: { p: '43 96% 40%', a: '45 93% 35%' }, dark: { p: '45 93% 47%', a: '43 96% 40%' } },
      green:  { light: { p: '142 71% 38%', a: '142 76% 30%' }, dark: { p: '142 71% 45%', a: '142 71% 38%' } },
      blue:   { light: { p: '221 83% 45%', a: '217 91% 50%' }, dark: { p: '217 91% 60%', a: '221 83% 53%' } },
      purple: { light: { p: '262 83% 50%', a: '263 70% 42%' }, dark: { p: '270 91% 65%', a: '262 83% 58%' } }
    };
    var pal = palettes[colorTheme];
    if (!pal) return;
    var c = pal[mode];

    var root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(mode);
    root.setAttribute('data-theme', mode);
    root.setAttribute('data-color-theme', colorTheme);
    root.style.setProperty('--primary', c.p);
    root.style.setProperty('--accent', c.a);
    root.style.setProperty('--ring', c.p);
    var fg = mode === 'dark' ? '0 0% 98%' : '0 0% 9%';
    root.style.setProperty('--primary-foreground', fg);
    root.style.setProperty('--accent-foreground', fg);
    // Marker for client code that wants to know the boot script ran.
    root.setAttribute('data-theme-booted', '1');
  } catch (e) { /* ignore */ }
})();
`;

const geistSans = localFont({
  src: "./fonts/GeistVF.woff",
  variable: "--font-geist-sans",
  weight: "100 900",
});
const geistMono = localFont({
  src: "./fonts/GeistMonoVF.woff",
  variable: "--font-geist-mono",
  weight: "100 900",
});

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Theme boot script — must be the first thing in <head> so the page
            paints in the user's theme colors before React hydrates. */}
        <script dangerouslySetInnerHTML={{ __html: themeBootScript }} />
        <meta charSet="UTF-8" />
        <meta name="viewport" content="initial-scale=1, width=device-width, viewport-fit=cover" />
        {/* Initial title matches BrandingState defaults; BrandingProvider replaces it
            client-side with the operator-configured productName + tagline on mount. */}
        <title>Smart Matrix - Crafting Tomorrow's Software Solutions</title>
        <meta name="description" content='Forward-thinking software house specializing in cutting-edge web applications, AI-powered solutions, and innovative digital experiences.' />
        
        {/* SEO Meta Tags */}
        <meta name="keywords" content="software development, web applications, AI solutions, software house, custom applications, modern web development, Kobotto" />
        <meta name="author" content="Kobotto" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Kobotto - Crafting Tomorrow's Software Solutions" />
        <meta property="og:description" content="Forward-thinking software house specializing in cutting-edge web applications, AI-powered solutions, and innovative digital experiences." />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Kobotto" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kobotto - Crafting Tomorrow's Software Solutions" />
        <meta name="twitter:description" content="Forward-thinking software house specializing in cutting-edge web applications, AI-powered solutions, and innovative digital experiences." />
        
        {/* Performance and Core Web Vitals */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data — suppressHydrationWarning prevents browser
            extensions (e.g. Grammarly, AI assistants) from breaking React
            hydration by injecting attributes into this script tag. */}
        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Kobotto",
              "description": "Forward-thinking software house specializing in cutting-edge web applications, AI-powered solutions, and innovative digital experiences.",
              "url": "https://kobotto.com",
              "logo": "https://kobotto.com/logo.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+1-555-123-4567",
                "contactType": "customer service",
                "email": "hello@kobotto.com"
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "San Francisco",
                "addressRegion": "CA",
                "addressCountry": "US"
              },
              "sameAs": [
                "https://linkedin.com/company/kobotto",
                "https://github.com/kobotto"
              ]
            })
          }}
        />
      </head>
      <body className={`${geistSans.variable} ${geistMono.variable} antialiased theme-transition theme-stable theme-container`} suppressHydrationWarning>
        <Provider store={store}>
          <BrandingProvider>
            <AuthProvider>
              <LanguageProvider>
                <ThemeProvider>
                  <NotificationProvider>
                    <ConfirmDialogProvider>
                      <CookieConsentProvider>
                        {children}
                        <ChatButton />
                      </CookieConsentProvider>
                    </ConfirmDialogProvider>
                  </NotificationProvider>
                </ThemeProvider>
              </LanguageProvider>
            </AuthProvider>
          </BrandingProvider>
        </Provider>
      </body>
    </html>
  );
}
